import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { PrintJobProvider } from './contexts/PrintJobContext';
import './i18n';

// Landing & General
import LandingPage from './pages/LandingPage';

// Admin Layout
import AdminLayout from './admin/AdminLayout';
import AdminLogin from './admin/pages/AdminLogin';

// Dashboard
import AdminDashboard from './admin/pages/AdminDashboard';
import SuperadminDashboard from './admin/pages/SuperadminDashboard';

// Reports
import AdminReports from './admin/pages/AdminReports';
import AdminReportsOrders from './admin/pages/reports/AdminReportsOrders';
import AdminReportsInvoices from './admin/pages/reports/AdminReportsInvoices';
import AdminReportsShifts from './admin/pages/reports/AdminReportsShifts';
import AdminReportsExports from './admin/pages/reports/AdminReportsExports';
import ComingSoon from './admin/pages/ComingSoon';

// Articles
import AdminArticleStudio from './admin/pages/articles/AdminArticleStudio';
import AdminArticleGroups from './admin/pages/articles/AdminArticleGroups';
import AdminArticleOptions from './admin/pages/articles/AdminArticleOptions';
import AdminArticleScreens from './admin/pages/articles/AdminArticleScreens';
import AdminArticleArrangement from './admin/pages/articles/AdminArticleArrangement';
import AdminArticlePricing from './admin/pages/articles/AdminArticlePricing';
import AdminArticleCourses from './admin/pages/articles/AdminArticleCourses';
import AdminProducts from './admin/pages/AdminProducts';
import AdminPrinters from './admin/pages/AdminPrinters';

// Venues
import AdminRooms from './admin/pages/venues/AdminRooms';
import AdminTables from './admin/pages/AdminTables';
import AdminMonitors from './admin/pages/venues/AdminMonitors';
import AdminRoomLayout from './admin/pages/venues/AdminRoomLayout';
import AdminSalesPoints from './admin/pages/venues/AdminSalesPoints';

// Basic data
import AdminSettings from './admin/pages/AdminSettings';
import AdminUsers from './admin/pages/AdminUsers';
import AdminReleases from './admin/pages/AdminReleases';
import AdminPaymentMethods from './admin/pages/basic/AdminPaymentMethods';
import AdminTaxTypes from './admin/pages/basic/AdminTaxTypes';
import AdminFiscal from './admin/pages/AdminFiscal';
import AdminKassenbuch from './admin/pages/AdminKassenbuch';
import AdminRoles from './admin/pages/AdminRoles';
import AdminCustomers from './admin/pages/AdminCustomers';
import AdminReservations from './admin/pages/AdminReservations';
import AdminSubscription from './admin/pages/AdminSubscription';
import {
  AdminAppCustomersHub,
  AdminLocationPresence,
  AdminLoyaltyCenter,
  AdminMenuHub,
  AdminNotifications,
  AdminOnlineOrdersHub,
  AdminOrderSettings,
  AdminPickupDisplay,
  AdminSetupStatus,
  AdminTerminals,
  AdminWebshop,
} from './admin/pages/manager/ManagerPortalPages';

// POS Terminal Pages
import PosLayout from './pos/PosLayout';
import PosLogin from './pos/pages/PosLogin';
import PosMobileHome from './pos/pages/PosMobileHome';
import PosTerminal from './pos/pages/PosTerminal';
import PosTables from './pos/pages/PosTables';
import PosOrders from './pos/pages/PosOrders';
import PosOnlineOrders from './pos/pages/PosOnlineOrders';
import PosInvoices from './pos/pages/PosInvoices';
import PosCashRegister from './pos/pages/PosCashRegister';
import PosReports from './pos/pages/PosReports';
import PosSettings from './pos/pages/PosSettingsPage';
import PosBerichtswesen from './pos/pages/PosBerichtswesen';
import PosReservations from './pos/pages/PosReservations';
import PosTimeTracking from './pos/pages/PosTimeTracking';

// Monitor Pages
import MonitorSelect from './monitor/MonitorSelect';
import KitchenTableMonitor from './monitor/KitchenTableMonitor';
import WebshopPage from './webshop/WebshopPage';

import '@/App.css';

function AdminRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (user?.role !== 'admin' && user?.role !== 'manager') return <Navigate to="/admin/login" replace />;
  return children;
}

function PosRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/pos/login" replace />;
  return children;
}

function MonitorRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/pos/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }
  return children;
}

function SuperadminRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated || user?.role !== 'superadmin') return <Navigate to="/admin/login" replace />;
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#1a2f38] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-slate-600 font-medium">Laden...</p>
      </div>
    </div>
  );
}

const Stub = ({ title, desc }) => <ComingSoon title={title} description={desc} />;

function PosIndexRedirect() {
  return <Navigate to="/pos/home" replace />;
}

function ReleasesRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen />;
  return <Navigate to={user?.role === 'superadmin' ? '/superadmin/releases' : '/admin/releases'} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/webshop/:slug" element={<WebshopPage />} />
      <Route path="/releases" element={<ReleasesRedirect />} />

      {/* ============== ADMIN PORTAL ============== */}
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* ============== SUPERADMIN PORTAL ============== */}
      <Route
        path="/superadmin"
        element={<SuperadminRoute><AdminLayout /></SuperadminRoute>}
      >
        <Route index element={<SuperadminDashboard />} />
        <Route path="releases" element={<AdminReleases />} />
      </Route>

      <Route
        path="/admin"
        element={<AdminRoute><AdminLayout /></AdminRoute>}
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />

        {/* Dashboard */}
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="releases" element={<AdminReleases />} />

        {/* Reports */}
        <Route path="reports" element={<AdminReports />} />
        <Route path="reports/shifts"   element={<AdminReportsShifts />} />
        <Route path="reports/invoices" element={<AdminReportsInvoices />} />
        <Route path="reports/orders"   element={<AdminReportsOrders />} />
        <Route path="reports/exports"  element={<AdminReportsExports />} />

        {/* Customers & Reservations (Sprint 9 — Kundenapp integration) */}
        <Route path="customers"    element={<AdminCustomers />} />
        <Route path="reservations" element={<AdminReservations />} />
        <Route path="location-presence" element={<AdminLocationPresence />} />
        <Route path="app-customers" element={<AdminAppCustomersHub />} />

        {/* Articles */}
        <Route path="articles"              element={<Navigate to="/admin/articles/studio" replace />} />
        <Route path="articles/studio"       element={<AdminArticleStudio />} />
        <Route path="articles/groups"       element={<AdminArticleGroups />} />
        <Route path="articles/products"     element={<AdminProducts />} />
        <Route path="articles/options"      element={<AdminArticleOptions />} />
        <Route path="articles/screens"      element={<AdminArticleScreens />} />
        <Route path="articles/arrangement"  element={<AdminArticleArrangement />} />
        <Route path="articles/print-groups" element={<AdminPrinters />} />
        <Route path="articles/pricing"      element={<AdminArticlePricing />} />
        <Route path="articles/courses"      element={<AdminArticleCourses />} />

        {/* Venues */}
        <Route path="venues"               element={<Stub title="Standorte" desc="Filialen und Standortverwaltung für multi-location Betrieb." />} />
        <Route path="venues/sales-points"  element={<AdminSalesPoints />} />
        <Route path="venues/rooms"         element={<AdminRooms />} />
        <Route path="venues/tables"        element={<AdminTables />} />
        <Route path="venues/room-layout"   element={<AdminRoomLayout />} />
        <Route path="venues/monitors"      element={<AdminMonitors />} />
        <Route path="venues/dispenser"     element={<Stub title="Dispenser" desc="Getränkeautomaten und Schankanlage Anbindung." />} />

        {/* Basic data */}
        <Route path="basic/company"          element={<AdminSettings />} />
        <Route path="basic/tax-types"        element={<AdminTaxTypes />} />
        <Route path="basic/payment-methods"  element={<AdminPaymentMethods />} />
        <Route path="basic/users"            element={<AdminUsers />} />
        <Route path="basic/user-roles"       element={<AdminRoles />} />
        <Route path="basic/fiscal"           element={<AdminFiscal />} />
        <Route path="basic/kassenbuch"       element={<AdminKassenbuch />} />
        <Route path="basic/subscription"     element={<AdminSubscription />} />

        {/* Online ordering */}
        <Route path="online-ordering/setup-status"  element={<AdminSetupStatus />} />
        <Route path="online-ordering/orders"        element={<AdminOnlineOrdersHub />} />
        <Route path="online-ordering/menus"         element={<AdminArticleStudio initialTab="menus" />} />
        <Route path="online-ordering/daily-menu"    element={<AdminMenuHub mode="daily" />} />
        <Route path="online-ordering/order-settings" element={<AdminOrderSettings />} />
        <Route path="online-ordering/webshop"        element={<AdminWebshop />} />
        <Route path="online-ordering/pickup-display" element={<AdminPickupDisplay />} />
        <Route path="online-ordering/terminals"      element={<AdminTerminals />} />

        {/* Loyalty & app */}
        <Route path="loyalty/cards"        element={<AdminLoyaltyCenter section="cards" />} />
        <Route path="loyalty/issued"       element={<AdminLoyaltyCenter section="issued" />} />
        <Route path="loyalty/coupons"      element={<AdminLoyaltyCenter section="coupons" />} />
        <Route path="loyalty/transactions" element={<AdminLoyaltyCenter section="transactions" />} />
        <Route path="app/notifications"    element={<AdminNotifications />} />
      </Route>

      {/* ============== POS TERMINAL ============== */}
      <Route path="/pos/login" element={<PosLogin />} />

      <Route
        path="/pos"
        element={<PosRoute><CartProvider><OfflineProvider><PosLayout /></OfflineProvider></CartProvider></PosRoute>}
      >
        <Route index element={<PosIndexRedirect />} />
        <Route path="home"            element={<PosMobileHome />} />
        <Route path="terminal"        element={<PosTerminal />} />
        <Route path="tables"          element={<PosTables />} />
        <Route path="orders"          element={<PosOrders />} />
        <Route path="online-orders"   element={<PosOnlineOrders />} />
        <Route path="invoices"        element={<PosInvoices />} />
        <Route path="cash-register"   element={<PosCashRegister />} />
        <Route path="reports"         element={<PosReports />} />
        <Route path="berichtswesen"   element={<PosBerichtswesen />} />
        <Route path="reservations"    element={<PosReservations />} />
        <Route path="time-tracking"   element={<PosTimeTracking />} />
        <Route path="settings"        element={<PosSettings />} />
      </Route>

      {/* ============== MONITOR ============== */}
      <Route path="/monitor" element={<MonitorRoute><MonitorSelect /></MonitorRoute>} />
      <Route path="/monitor/:profileId" element={<MonitorRoute><KitchenTableMonitor /></MonitorRoute>} />

      <Route path="*" element={<Navigate to="/pos/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      {/* Prints the jobs routed to this device's printers, on every page and independent of the waiter login. */}
      <PrintJobProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </PrintJobProvider>
    </AuthProvider>
  );
}

export default App;
